import { AppProviderContext } from '@websrc/App/AppContext'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapMSForm } from '@websrc/DataMappers/mapSpot'
import React, { useContext, useEffect, useRef } from 'react'

export interface MSFormProps {
  formScript: ReturnType<typeof mapMSForm>['ms_form']['script']
}

/**
 *  DANGER: there is some security vulnerabilities ahead
 *  MSForm is a marketing form from Microsoft CRM
 *  The user is supposed to copy paste html + script
 *  and the component should run whatever is there
 */

export function MSForm({ formScript }: MSFormProps) {
  const divRef = useRef<HTMLDivElement>(null)
  const { matomoPageTemplate } = useContext(AppProviderContext)

  useEffect(() => {
    if (!formScript) return

    const iframeHtml = document.createRange().createContextualFragment(formScript)

    if (divRef.current) {
      divRef.current.innerHTML = '' // empty first

      const iframe = iframeHtml.querySelector('iframe')

      if (!iframe) {
        return
      }

      // Add the HTML before fixing the source
      // because there might be a script in there that sets the "src" on the iframe
      divRef.current.appendChild(iframeHtml) // add nasty script after

      // So we can see the form when running locally
      if (location.hostname !== 'localhost') {
        const iframeSrc = iframe.src

        const crmUrl = new URL(iframeSrc)

        // prefix /crm so it hits our crm edge lambda
        const relativePath = `/crm${crmUrl.pathname}`

        iframe.src = relativePath
      }

      // wait for the iframe to be loaded
      const iframeLoadedListener = () => {
        let formCheckInterval: NodeJS.Timeout

        // the form gets added to the iframe after some time
        const formChecker = () => {
          const form = iframe.contentDocument?.querySelector('form')

          if (form) {
            clearInterval(formCheckInterval)

            // look for first text content inside the form and use it as form name
            const formName =
              form.querySelectorAll('span, p, h1, h2, h3, h4, h5, h6').item(0)?.textContent ??
              'unknown form'

            // listen for the form to be submitted
            form.addEventListener('submit', () => {
              matomo.trackEvent('engagement', matomoPageTemplate, [formName], [], 'form')

              window.gtag?.('event', 'form submission', {
                form_name: formName,
              })

              // once the form has been submitted, the user might be able to reload it and submit it again, so we need to start checking for the form again
              formCheckInterval = setInterval(formChecker, 2000)
            })
          }
        }

        formCheckInterval = setInterval(formChecker, 2000)
      }

      iframe.addEventListener('load', iframeLoadedListener)

      return () => iframe.removeEventListener('load', iframeLoadedListener)
    }
  }, [formScript, matomoPageTemplate])

  return <div ref={divRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
}
